import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  ClearUserDevice,
  GetUsersNotInEvent, GetTeamByEventId, getUserDetails,
  AddUsers, UpdateUser, AddEvt_UserOrganiserMapping,
  AddTeamMember, GetUserEventPermissions, DeleteTeamMember,
} from '../../api/api';
import { isMarriageEventType } from '../../utils/helpers';

export const MODULES = ['Events', 'Functions', 'Users', 'Members', 'Services', 'Expenses'];
export const MODULE_LABELS = {
  Events: 'Events',
  Functions: 'Functions',
  Users: 'Members',
  Members: 'Guests',
  Services: 'Services (caterer, event manager …)',
  Expenses: 'Expenses & payments',
};
export const ACTIONS = ['', 'Add', 'Edit', 'Delete']; // '' = View
export const REPORT_BITS = [];
const ALL_BITS = [...MODULES.flatMap(m => ACTIONS.map(a => `Can${m}${a}`)), ...REPORT_BITS.map(([b]) => b)];

const blankPerms = () => Object.fromEntries(ALL_BITS.map(b => [b, 0]));
// New plain user = an "event member" who invites guests AND can add more users
// (the invite chain grows without an admin). They manage only what they create.
const defaultUserPerms = () => {
  const p = blankPerms();
  p.CanMembers = 1; p.CanMembersAdd = 1; p.CanMembersEdit = 1;
  p.CanUsers = 1; p.CanUsersAdd = 1; p.CanUsersDelete = 1;
  return p;
};
const permsFromRow = (row) => Object.fromEntries(ALL_BITS.map(b => [b, row[b] ? 1 : 0]));

export function useUsers() {
  const { user, organiser, event, isAdmin, isSuperAdmin, can } = useAuth();
  // Marriage events are two-sided: every ordinary member belongs to exactly ONE
  // side and may only add guests for it (admins keep both sides). The side is
  // stored on their per-event mapping row (Evt_UserEventsMapping.Side).
  const isMarriage = isMarriageEventType(event?.EventTypeName || event?.EventType);
  const toast = useToast();
  const me = user?.UserId;
  const orgId = organiser?.OrganiserId;
  const eventId = event?.EventId;
  const ownerId = organiser?.OrganiserMasterUser;
  // Permissions are PER-EVENT now: the matrix writes to Evt_UserEventsMapping for
  // the selected event. Without an event picked there is nothing to edit.
  const needEvent = !eventId;

  // Anyone with the Users bits can open the screen; admins always can.
  const canViewUsers = isAdmin || can('CanUsers');
  const canAddUsers = isAdmin || can('CanUsersAdd');
  // Non-admins may manage (remove) only the users they themselves added.
  const canManage = (row) => isAdmin || String(row.CreatedBy) === String(me);

  const [members, setMembers] = useState([]);
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'add' | 'edit' | null
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [form, setForm] = useState({});

  const refresh = useCallback(() => {
    if (!eventId) {
      setMembers([]);
      setAvailable([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      GetTeamByEventId({ EventId: eventId }),
      GetUsersNotInEvent({ EventId: eventId }),
    ]).then(([m, a]) => {
      setMembers(Array.isArray(m) ? m : []);
      setAvailable(Array.isArray(a) ? a : []);
    }).catch(() => toast('Failed to load users')).finally(() => setLoading(false));
  }, [eventId, toast]);

  useEffect(() => { refresh(); }, [refresh]);

  const roleOf = (row) =>
    Number(row.SuperAdmin) === 1 ? 'Super Admin'
      : row.UserId === ownerId ? 'Event Admin'
        : Number(row.IsAdmin) === 1 ? 'Event Admin' : 'Member';
  const roleChip = (role) =>
    role === 'Super Admin' ? 'chip male'
      : role === 'Event Admin' ? 'chip warn' : 'chip pend';

  const openAdd = () => {
    setForm({ addMode: 'existing', UserId: '', Name: '', Surname: '', MobileNumber: '', Password: '', IsAdmin: false, Side: '', Designation: '', perms: defaultUserPerms() });
    setErr(''); setEditing(null); setModal('add');
  };
  const openEdit = (row) => {
    // Prefill from the user's per-event permission row (Evt_UserEventsMapping)
    // for the selected event — falls back to blanks if they have none yet.
    setForm({ IsAdmin: false, Side: row.Side && row.Side !== 'Common' ? row.Side : '', Designation: row.Designation || '', perms: blankPerms() });
    setErr(''); setEditing(row); setModal('edit');
    if (eventId) {
      GetUserEventPermissions({ EventId: eventId, UserId: row.UserId })
        .then(rows => {
          const er = Array.isArray(rows) ? rows[0] : (rows || null);
          if (er) setForm(f => ({ ...f, IsAdmin: er.IsAdmin === 1, Side: er.Side && er.Side !== 'Common' ? er.Side : f.Side, perms: permsFromRow(er) }));
        })
        .catch(() => {});
    }
  };

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setPerm = (bit, val) => setForm(f => ({ ...f, perms: { ...f.perms, [bit]: val ? 1 : 0 } }));

  // Upsert the user's per-event permission row (Evt_UserEventsMapping is keyed by
  // UserId + EventId, and the backend Add endpoint upserts on that pair). IsAdmin
  // here = admin of THIS event; perms are this event's granular Can* bits.
  const saveEventPerms = (userId) => {
    const plain = !isAdmin; // non-admins can only grant the default member perms
    return AddTeamMember({
      UserId: userId, EventId: eventId, OrganiserId: orgId,
      IsAdmin: plain ? 0 : (form.IsAdmin ? 1 : 0),
      EventRole: plain ? 'Member' : (form.IsAdmin ? 'Event Admin' : 'Member'),
      // Admins work across both sides ('Common'); a member is pinned to one.
      Side: !isMarriage || form.IsAdmin ? 'Common' : form.Side,
      ...(plain ? defaultUserPerms() : form.perms),
      Valid: 1, CreatedBy: me, UpdatedBy: me,
    });
  };

  const save = async () => {
    setErr('');
    if (needEvent) return setErr('Pick an event first — permissions are set per event.');
    if (isMarriage && !form.IsAdmin && !form.Side) return setErr('Pick the side (Groom or Bride) this member invites for.');
    try {
      setSaving(true);
      if (modal === 'add') {
        let userId;
        if (form.addMode === 'new') {
          if (!form.Name.trim()) return setErr('Enter a name');
          if (!/^\d{10}$/.test(form.MobileNumber)) return setErr('Enter a valid 10-digit mobile');
          if (!form.Password) return setErr('Set a password');
          const ex = await getUserDetails({ MobileNumber: form.MobileNumber });
          if (Array.isArray(ex) && ex.length > 0) return setErr('That mobile is already a user — add them via "Existing user".');
          const r = await AddUsers({
            MobileNumber: form.MobileNumber, Password: form.Password, Name: form.Name, Surname: form.Surname,
            SuperAdmin: 0, AllowUserMobile: 1, Valid: 1, CreatedBy: me, UpdatedBy: me,
          });
          userId = r?.insertId;
          if (!userId) return setErr('Could not create the user');
        } else {
          userId = Number(form.UserId);
          if (!userId) return setErr('Pick a user to add');
        }
        // Org mapping grants ACCESS to the organiser (so they appear in the list
        // and can sign in / pick this organiser). Granular permissions no longer
        // live here — they go on the per-event mapping below. Org-wide IsAdmin is
        // left 0; promote to org-wide admin elsewhere if ever needed.
        try {
          await AddEvt_UserOrganiserMapping({
            UserId: userId, OrganiserId: orgId,
            IsAdmin: 0, Designation: form.Designation,
            Valid: 1, CreatedBy: me, UpdatedBy: me,
          });
        } catch (mapErr) {
          if (form.addMode === 'new') throw mapErr;
        }
        await saveEventPerms(userId);
        toast('User added with permissions for this event');
      } else {
        await saveEventPerms(editing.UserId);
        toast('Permissions updated for this event');
      }
      setModal(null);
      refresh();
    } catch {
      setErr('Save failed. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  // Device binding summary for the list: which device(s) hold this account.
  // Three device slots per account: the APK, a browser on that same phone, and a
  // desktop browser. The two phone ones are paired server-side (one OTP unlocks
  // both), but each still keeps its own id, model and bound date.
  const deviceInfo = (row) => {
    const slots = [
      { on: !!row.MobileDeviceId,    icon: '📱', what: 'app',     model: row.DeviceModel,     since: row.DeviceBoundDate },
      { on: !!row.MobileWebDeviceId, icon: '📲', what: 'phone web', model: row.MobileWebModel,  since: row.MobileWebBoundDate },
      // The desktop slot holds one token per browser of that laptop.
      { on: !!row.DeviceName,        icon: '💻',
        what: (() => {
          const count = String(row.DeviceName || '').split(',').filter(Boolean).length;
          return count > 1 ? `desktop ×${count}` : 'desktop';
        })(),
        model: row.DeviceNameModel, since: row.DeviceNameBoundDate },
    ].filter(s => s.on);
    return {
      bound: slots.length > 0,
      label: slots.map(s => `${s.icon} ${s.what}`).join(' + ') || 'Not bound',
      model: slots.map(s => s.model).filter(Boolean).join(' · '),
      since: slots.map(s => s.since).filter(Boolean).sort().pop() || null,
      slots,
    };
  };
  // Super Admin only: unbind phone AND browser. The user's current device is
  // signed out by DeviceGuard within a minute; the next sign-in binds fresh.
  const canResetDevice = (row) => isSuperAdmin && deviceInfo(row).bound;
  const clearDevice = async (row) => {
    if (!window.confirm(`Reset the device for ${row.Name || 'this user'}?

Their current phone/browser will be signed out and the next sign-in (on any device) becomes the new one.`)) return;
    try {
      await ClearUserDevice({ UserId: row.UserId });
      toast('Device reset — user can sign in on a new device'); refresh();
    } catch { toast('Failed to reset device'); }
  };

  // Activate / deactivate a user. Deactivated (IsActive=0) users are blocked at
  // their next login. Owner/self/super-admin can't be deactivated here.
  const canToggleActive = (row) => isAdmin && row.UserId !== ownerId && row.UserId !== me && row.SuperAdmin !== 1;
  const isActive = (row) => row.IsActive !== 0; // default-active when column missing
  const toggleActive = async (row) => {
    const next = isActive(row) ? 0 : 1;
    const verb = next === 0 ? 'Deactivate' : 'Activate';
    if (!window.confirm(`${verb} ${row.Name || 'this user'}?`)) return;
    try {
      await UpdateUser({ data: { IsActive: next, UpdatedBy: me }, where: `UserId = ${row.UserId}` });
      toast(next === 0 ? 'User deactivated' : 'User activated'); refresh();
    } catch { toast('Failed to update status'); }
  };

  // Removable if it's not the owner / self / a super admin, AND the caller is
  // allowed to manage this row (admin, or a non-admin who added them).
  const canRemove = (row) => row.UserId !== ownerId && row.UserId !== me && row.SuperAdmin !== 1 && canManage(row);
  const remove = async (row) => {
    if (!window.confirm(`Remove ${row.Name || 'this user'} from ${event?.EventName || 'this event'}?`)) return;
    try {
      if (row.MappingId) {
        await DeleteTeamMember({ where: `MappingId = ${row.MappingId}` });
      } else {
        await DeleteTeamMember({ where: `EventId = ${eventId} and UserId = ${row.UserId}` });
      }
      toast('Removed'); refresh();
    } catch { toast('Remove failed'); }
  };

  // Non-admins see only the users they added; admins see everyone.
  const visibleMembers = isAdmin ? members : members.filter(m => String(m.CreatedBy) === String(me));

  return {
    isAdmin, canViewUsers, canAddUsers, canManage, organiser, event, needEvent, members: visibleMembers,
    available, loading, modal, setModal, editing, saving, err, form,
    isMarriage, roleOf, roleChip, openAdd, openEdit, setF, setPerm, save, clearDevice, canResetDevice, deviceInfo, canRemove, remove,
    isActive, canToggleActive, toggleActive,
  };
}
